import type { Screen } from '@ghostscreen/contracts';
import {
  getScreenConnectionStatus,
  getScreenSyncStatus,
  type ScreenConnectionStatus,
  type ScreenSyncStatus,
} from '../screen-health';

/** Connection status as shown on display cards (includes unpaired). */
export type CardConnectionStatus = ScreenConnectionStatus | 'unpaired';

/** Sync states we can honestly show from published Screen fields today. */
export type CardSyncStatus = Extract<ScreenSyncStatus, 'up-to-date' | 'outdated' | 'unknown'>;

export type ScreenConnectionCounts = {
  online: number;
  offline: number;
  unknown: number;
  unpaired: number;
  total: number;
};

/**
 * Card connection status from Screen health fields.
 * Unpaired is explicit — never folded into unknown.
 */
export function getCardConnectionStatus(screen: Screen): CardConnectionStatus {
  if (screen.deviceId == null) return 'unpaired';
  return getScreenConnectionStatus({ lastSeenAt: screen.lastHeartbeatAt });
}

/**
 * Card sync status from Screen version fields.
 * `error` / `updating` are not shown — no data source supplies them yet.
 */
export function getCardSyncStatus(screen: Screen): CardSyncStatus {
  const status = getScreenSyncStatus({
    desiredPlaylistVersion: screen.desiredPlaylistVersion,
    reportedPlaylistVersion: screen.reportedPlaylistVersion,
  });
  return status === 'error' || status === 'updating' ? 'unknown' : status;
}

/** Page-level counts using the same rules as display cards. */
export function deriveScreenConnectionCounts(screens: Screen[]): ScreenConnectionCounts {
  const counts: ScreenConnectionCounts = {
    online: 0,
    offline: 0,
    unknown: 0,
    unpaired: 0,
    total: screens.length,
  };

  for (const screen of screens) {
    const status = getCardConnectionStatus(screen);
    counts[status] += 1;
  }

  return counts;
}
